import React, { useState, useEffect, useRef } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Zap, Globe, Plus, ExternalLink, Search, Filter, SlidersHorizontal, X, RotateCcw, BookOpen, Layers, GraduationCap, Clock, FileText, Users, Calendar, Coins, CheckCircle2, FileEdit, Archive } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { MultiSelectPopover, Option } from '@/components/shared/MultiSelectPopover'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'

interface FilterOption { id: number | string; name: string }

interface TestItem {
  id: number
  title: string
  test_type: string
  language: string
  status: string
  is_free: boolean
  credit_cost: number
  question_count: number
  duration_minutes: number
  exam: string | null
  created_by: string | null
  mapped_exams?: FilterOption[]
  mapped_subjects?: FilterOption[]
  mapped_chapters?: FilterOption[]
  mapped_sections?: FilterOption[]
  mapped_standards?: FilterOption[]
  attempts_count?: number
  unique_users_count?: number
  total_credits_generated?: number
  activated_at?: string
}

interface ChapterOption { id: number; name: string; subject_id: number }

interface Filters {
  statuses: string[]
  test_types: string[]
  languages: string[]
  access_types: string[]
  duration_ranges: string[]
  exams: FilterOption[]
  subjects: FilterOption[]
  chapters: ChapterOption[]
  standards: FilterOption[]
  syllabi: FilterOption[]
}

interface Meta extends PaginationMeta {}

interface Props {
  tests: TestItem[]
  meta?: Meta
  filters?: Filters
  // Fallbacks for older props
  exams?: FilterOption[]
  subjects?: FilterOption[]
  chapters?: ChapterOption[]
}

const DURATION_LABELS: Record<string, string> = {
  short: 'Short (<30m)',
  medium: 'Medium (30-60m)',
  standard: 'Standard (60-180m)',
  long: 'Long (>180m)'
}

interface TestContextBadgesProps {
  mappedExams?: FilterOption[]
  mappedSubjects?: FilterOption[]
  mappedChapters?: FilterOption[]
  mappedSections?: FilterOption[]
  mappedStandards?: FilterOption[]
  attemptsCount?: number
  uniqueUsersCount?: number
  totalCreditsGenerated?: number
  activatedAt?: string
  returnUrl: string
  isLastRow?: boolean
  isFirstRow?: boolean
}

function TestContextBadges({
  mappedExams = [],
  mappedSubjects = [],
  mappedChapters = [],
  mappedSections = [],
  mappedStandards = [],
  attemptsCount = 0,
  uniqueUsersCount = 0,
  totalCreditsGenerated = 0,
  activatedAt,
  returnUrl,
  isLastRow = false,
  isFirstRow = false,
}: TestContextBadgesProps) {
  const [activeTab, setActiveTab] = useState<'exams' | 'subjects' | 'chapters' | 'sections' | 'standards' | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setActiveTab(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleTab = (tab: 'exams' | 'subjects' | 'chapters' | 'sections' | 'standards') => {
    setActiveTab((prev) => (prev === tab ? null : tab))
  }

  const cleanReturnUrl = returnUrl ? returnUrl.replace(/^https?:\/\/[^\/]+/, '') : '/admin/tests'

  const activeItems =
    activeTab === 'exams'
      ? { title: 'Exams', linkPrefix: '/admin/exams', list: mappedExams }
      : activeTab === 'subjects'
      ? { title: 'Subjects', linkPrefix: '/admin/subjects', list: mappedSubjects }
      : activeTab === 'chapters'
      ? { title: 'Chapters', linkPrefix: '/admin/chapters', list: mappedChapters }
      : activeTab === 'sections'
      ? { title: 'Sections', linkPrefix: '/admin/sections', list: mappedSections }
      : activeTab === 'standards'
      ? { title: 'Standards', linkPrefix: '/admin/standards', list: mappedStandards }
      : null

  const openUpward = isLastRow && !isFirstRow

  return (
    <div className="relative inline-flex flex-wrap items-center gap-1.5 pt-1" ref={containerRef}>
      {mappedExams.length > 0 && (
        <Badge
          variant="outline"
          onClick={() => toggleTab('exams')}
          className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
            activeTab === 'exams' ? 'bg-indigo-100 text-indigo-900 border-indigo-300 font-medium dark:bg-indigo-950 dark:text-indigo-200' : 'bg-background/50 hover:bg-accent'
          }`}
        >
          <GraduationCap className="h-3 w-3 text-indigo-500" />
          <span>{mappedExams.length} {mappedExams.length === 1 ? mappedExams[0].name : 'Exams'}</span>
        </Badge>
      )}

      {mappedSubjects.length > 0 && (
        <Badge
          variant="outline"
          onClick={() => toggleTab('subjects')}
          className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
            activeTab === 'subjects' ? 'bg-emerald-100 text-emerald-900 border-emerald-300 font-medium dark:bg-emerald-950 dark:text-emerald-200' : 'bg-background/50 hover:bg-accent'
          }`}
        >
          <BookOpen className="h-3 w-3 text-emerald-500" />
          <span>{mappedSubjects.length} Subjects</span>
        </Badge>
      )}

      {mappedChapters.length > 0 && (
        <Badge
          variant="outline"
          onClick={() => toggleTab('chapters')}
          className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
            activeTab === 'chapters' ? 'bg-blue-100 text-blue-900 border-blue-300 font-medium dark:bg-blue-950 dark:text-blue-200' : 'bg-background/50 hover:bg-accent'
          }`}
        >
          <BookOpen className="h-3 w-3 text-blue-500" />
          <span>{mappedChapters.length} Chapters</span>
        </Badge>
      )}

      {mappedSections.length > 0 && (
        <Badge
          variant="outline"
          onClick={() => toggleTab('sections')}
          className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
            activeTab === 'sections' ? 'bg-cyan-100 text-cyan-900 border-cyan-300 font-medium dark:bg-cyan-950 dark:text-cyan-200' : 'bg-background/50 hover:bg-accent'
          }`}
        >
          <Layers className="h-3 w-3 text-cyan-500" />
          <span>{mappedSections.length} Sections</span>
        </Badge>
      )}

      {mappedStandards.length > 0 && (
        <Badge
          variant="outline"
          onClick={() => toggleTab('standards')}
          className={`gap-1 text-[10px] px-1.5 py-0 cursor-pointer font-normal transition-colors ${
            activeTab === 'standards' ? 'bg-purple-100 text-purple-900 border-purple-300 font-medium dark:bg-purple-950 dark:text-purple-200' : 'bg-background/50 hover:bg-accent'
          }`}
        >
          <Layers className="h-3 w-3 text-purple-500" />
          <span>{mappedStandards.length} Standards</span>
        </Badge>
      )}

      {/* Attempts Badge - Only shown if attemptsCount > 0 */}
      {attemptsCount > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] px-1.5 py-0 font-normal bg-background/50 text-muted-foreground">
          <Users className="h-3 w-3 text-muted-foreground" />
          <span>{attemptsCount} {attemptsCount === 1 ? 'Attempt' : 'Attempts'} ({uniqueUsersCount} {uniqueUsersCount === 1 ? 'User' : 'Users'})</span>
        </Badge>
      )}

      {/* Credits Generated Badge - Only shown if totalCreditsGenerated > 0 */}
      {totalCreditsGenerated > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] px-1.5 py-0 font-normal bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200">
          <Coins className="h-3 w-3 text-amber-500" />
          <span>{totalCreditsGenerated} Credits</span>
        </Badge>
      )}

      {/* Activation Date Badge */}
      {activatedAt && (
        <Badge variant="outline" className="gap-1 text-[10px] px-1.5 py-0 font-normal bg-background/50 text-muted-foreground">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span>{activatedAt}</span>
        </Badge>
      )}

      {/* Unified Popover Panel */}
      {activeItems && activeItems.list.length > 0 && (
        <div
          className={`absolute left-0 w-60 rounded-md border bg-popover text-popover-foreground shadow-xl z-[100] p-2.5 text-xs space-y-1.5 animate-in fade-in-50 zoom-in-95 ${
            openUpward ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
        >
          <div className="font-semibold text-[11px] text-muted-foreground pb-1.5 border-b flex justify-between items-center">
            <span className="font-semibold text-foreground">Mapped {activeItems.title} ({activeItems.list.length})</span>
            <X className="h-3.5 w-3.5 cursor-pointer hover:text-foreground text-muted-foreground" onClick={() => setActiveTab(null)} />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-0.5 pt-0.5">
            {activeItems.list.map((item) => (
              <Link
                key={item.id}
                href={`${activeItems.linkPrefix}/${item.id}?return_to=${encodeURIComponent(cleanReturnUrl)}`}
                className="flex items-center justify-between px-2.5 py-1.5 rounded-sm hover:bg-accent hover:text-accent-foreground text-primary font-medium text-xs truncate transition-colors"
              >
                <span className="truncate">{item.name || `#${item.id}`}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0 ml-2" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function TestsIndex({
  tests, meta, filters, exams: rawExams = [], subjects: rawSubjects = [], chapters: rawChapters = []
}: Props) {
  const getArrayParam = (key: string): string[] => {
    if (typeof window === 'undefined') return []
    const sp = new URLSearchParams(window.location.search)
    const values: string[] = []
    sp.forEach((val, k) => {
      if (k === key || k.startsWith(`${key}[`)) {
        if (val && !values.includes(val)) values.push(val)
      }
    })
    return values
  }

  const currentParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()

  const [search, setSearch] = useState(currentParams.get('q') || '')
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(getArrayParam('statuses'))
  const [selectedTypes, setSelectedTypes] = useState<string[]>(getArrayParam('test_types'))
  const [selectedExams, setSelectedExams] = useState<string[]>(getArrayParam('exam_ids'))
  const [selectedLangs, setSelectedLangs] = useState<string[]>(getArrayParam('languages'))
  const [selectedAccess, setSelectedAccess] = useState<string[]>(getArrayParam('access_types'))
  const [selectedDurations, setSelectedDurations] = useState<string[]>(getArrayParam('duration_ranges'))
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(getArrayParam('subject_ids'))
  const [selectedChapters, setSelectedChapters] = useState<string[]>(getArrayParam('chapter_ids'))
  const [selectedStandards, setSelectedStandards] = useState<string[]>(getArrayParam('standard_ids'))
  const [selectedSyllabi, setSelectedSyllabi] = useState<string[]>(getArrayParam('syllabus_ids'))

  const [drawerOpen, setDrawerOpen] = useState(false)

  // Options lists
  const availableExams: Option[] = (filters?.exams || rawExams).map((e) => ({ id: e.id, label: e.name }))
  const availableSubjects: Option[] = (filters?.subjects || rawSubjects).map((s) => ({ id: s.id, label: s.name }))
  const availableChaptersAll = filters?.chapters || rawChapters
  const availableStandards: Option[] = (filters?.standards || []).map((st) => ({ id: st.id, label: st.name }))
  const availableSyllabi: Option[] = (filters?.syllabi || []).map((sy) => ({ id: sy.id, label: sy.name }))

  // Cascading chapters
  const filteredChapterOptions: Option[] = availableChaptersAll
    .filter((c) => selectedSubjects.length === 0 || selectedSubjects.includes(String(c.subject_id)))
    .map((c) => ({ id: c.id, label: c.name }))

  const statusOptions: Option[] = (filters?.statuses || ['draft', 'published', 'archived']).map((s) => ({
    id: s,
    label: s.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
  }))

  const TEST_TYPE_LABELS: Record<string, string> = {
    full_mock: 'Full Mock',
    sectional: 'Sectional',
    chapter_test: 'Chapter Test',
    practice: 'Practice',
  }

  const typeOptions: Option[] = (filters?.test_types || ['full_mock', 'sectional', 'chapter_test', 'practice']).map((t) => ({
    id: t,
    label: TEST_TYPE_LABELS[t] || t.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
  }))

  const langOptions: Option[] = [
    { id: 'en', label: 'English (EN)' },
    { id: 'hi', label: 'Hindi (HI)' }
  ]

  const accessOptions: Option[] = [
    { id: 'free', label: 'Free' },
    { id: 'paid', label: 'Paid / Credits' }
  ]

  const durationOptions: Option[] = [
    { id: 'short', label: 'Short (<30m)' },
    { id: 'medium', label: 'Medium (30-60m)' },
    { id: 'standard', label: 'Standard (60-180m)' },
    { id: 'long', label: 'Long (>180m)' }
  ]

  const applyFilters = (overrides?: Record<string, any>) => {
    const query: Record<string, any> = {}

    const qVal = overrides && 'q' in overrides ? overrides.q : search
    if (qVal) query.q = qVal

    const stVal = overrides && 'statuses' in overrides ? overrides.statuses : selectedStatuses
    if (stVal.length > 0) query['statuses[]'] = stVal

    const ttVal = overrides && 'test_types' in overrides ? overrides.test_types : selectedTypes
    if (ttVal.length > 0) query['test_types[]'] = ttVal

    const exVal = overrides && 'exam_ids' in overrides ? overrides.exam_ids : selectedExams
    if (exVal.length > 0) query['exam_ids[]'] = exVal

    const langVal = overrides && 'languages' in overrides ? overrides.languages : selectedLangs
    if (langVal.length > 0) query['languages[]'] = langVal

    const accVal = overrides && 'access_types' in overrides ? overrides.access_types : selectedAccess
    if (accVal.length > 0) query['access_types[]'] = accVal

    const durVal = overrides && 'duration_ranges' in overrides ? overrides.duration_ranges : selectedDurations
    if (durVal.length > 0) query['duration_ranges[]'] = durVal

    const subjVal = overrides && 'subject_ids' in overrides ? overrides.subject_ids : selectedSubjects
    if (subjVal.length > 0) query['subject_ids[]'] = subjVal

    const chapVal = overrides && 'chapter_ids' in overrides ? overrides.chapter_ids : selectedChapters
    if (chapVal.length > 0) query['chapter_ids[]'] = chapVal

    const stdVal = overrides && 'standard_ids' in overrides ? overrides.standard_ids : selectedStandards
    if (stdVal.length > 0) query['standard_ids[]'] = stdVal

    const sylVal = overrides && 'syllabus_ids' in overrides ? overrides.syllabus_ids : selectedSyllabi
    if (sylVal.length > 0) query['syllabus_ids[]'] = sylVal

    const pageVal = overrides && 'page' in overrides ? overrides.page : undefined
    if (pageVal) query.page = pageVal

    const perPageVal = overrides && 'per_page' in overrides ? overrides.per_page : (currentParams.get('per_page') || (meta?.per_page ? String(meta.per_page) : undefined))
    if (perPageVal) query.per_page = perPageVal

    router.get(window.location.pathname, query, { preserveState: true, replace: true })
  }

  const clearAllFilters = () => {
    setSearch('')
    setSelectedStatuses([])
    setSelectedTypes([])
    setSelectedExams([])
    setSelectedLangs([])
    setSelectedAccess([])
    setSelectedDurations([])
    setSelectedSubjects([])
    setSelectedChapters([])
    setSelectedStandards([])
    setSelectedSyllabi([])
    router.get(window.location.pathname, {}, { preserveState: true, replace: true })
  }

  const advancedFiltersCount =
    selectedAccess.length +
    selectedDurations.length +
    selectedSubjects.length +
    selectedChapters.length +
    selectedStandards.length +
    selectedSyllabi.length

  const totalActiveFiltersCount =
    (search ? 1 : 0) +
    selectedStatuses.length +
    selectedTypes.length +
    selectedExams.length +
    selectedLangs.length +
    advancedFiltersCount

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Tests Management</h1>
            <p className="text-muted-foreground mt-1">Generate, configure, and publish test papers</p>
          </div>
          <Button asChild className="gap-2 font-bold shadow-xs">
            <Link href="/admin/test_generator">
              <Zap className="h-4 w-4" />
              Generate Test
            </Link>
          </Button>
        </div>

        {/* Filters Section */}
        <div className="space-y-3 bg-card border rounded-lg p-4 shadow-sm">
          {/* Top Filter Bar */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search title or description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  applyFilters({ q: e.target.value })
                }}
                className="pl-8 h-9 text-xs"
              />
            </div>

            <MultiSelectPopover
              title="Status"
              options={statusOptions}
              selectedValues={selectedStatuses}
              onSelectionChange={(newVal) => {
                setSelectedStatuses(newVal)
                applyFilters({ statuses: newVal })
              }}
              icon={<Filter className="h-3.5 w-3.5 text-muted-foreground" />}
            />

            <MultiSelectPopover
              title="Test Type"
              options={typeOptions}
              selectedValues={selectedTypes}
              onSelectionChange={(newVal) => {
                setSelectedTypes(newVal)
                applyFilters({ test_types: newVal })
              }}
              icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
            />

            <MultiSelectPopover
              title="Exam"
              options={availableExams}
              selectedValues={selectedExams}
              onSelectionChange={(newVal) => {
                setSelectedExams(newVal)
                applyFilters({ exam_ids: newVal })
              }}
              icon={<GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />}
            />

            <MultiSelectPopover
              title="Standard"
              options={availableStandards}
              selectedValues={selectedStandards}
              onSelectionChange={(newVal) => {
                setSelectedStandards(newVal)
                applyFilters({ standard_ids: newVal })
              }}
              icon={<Layers className="h-3.5 w-3.5 text-muted-foreground" />}
            />

            <MultiSelectPopover
              title="Subject"
              options={availableSubjects}
              selectedValues={selectedSubjects}
              onSelectionChange={(newVal) => {
                setSelectedSubjects(newVal)
                setSelectedChapters([])
                applyFilters({ subject_ids: newVal, chapter_ids: [] })
              }}
              icon={<BookOpen className="h-3.5 w-3.5 text-muted-foreground" />}
            />

            <MultiSelectPopover
              title="Chapter"
              options={filteredChapterOptions}
              selectedValues={selectedChapters}
              onSelectionChange={(newVal) => {
                setSelectedChapters(newVal)
                applyFilters({ chapter_ids: newVal })
              }}
              icon={<BookOpen className="h-3.5 w-3.5 text-muted-foreground" />}
            />

            <MultiSelectPopover
              title="Language"
              options={langOptions}
              selectedValues={selectedLangs}
              onSelectionChange={(newVal) => {
                setSelectedLangs(newVal)
                applyFilters({ languages: newVal })
              }}
              icon={<Globe className="h-3.5 w-3.5 text-muted-foreground" />}
            />

            <Button
              variant={drawerOpen || advancedFiltersCount > 0 ? "secondary" : "outline"}
              size="sm"
              onClick={() => setDrawerOpen(!drawerOpen)}
              className="h-9 gap-1.5 text-xs font-normal"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>All Filters</span>
              {advancedFiltersCount > 0 && (
                <Badge variant="default" className="h-4 px-1 text-[10px] ml-1">
                  {advancedFiltersCount}
                </Badge>
              )}
            </Button>

            {totalActiveFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-9 text-xs text-muted-foreground hover:text-foreground gap-1"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Clear All
              </Button>
            )}
          </div>

          {/* Expandable Advanced Filter Drawer */}
          {drawerOpen && (
            <div className="pt-3 mt-3 border-t grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 animate-in fade-in-50">
              <div>
                <Label className="text-[11px] text-muted-foreground mb-1 block">Subject</Label>
                <MultiSelectPopover
                  title="Select Subjects"
                  options={availableSubjects}
                  selectedValues={selectedSubjects}
                  onSelectionChange={(newVal) => {
                    setSelectedSubjects(newVal)
                    setSelectedChapters([])
                    applyFilters({ subject_ids: newVal, chapter_ids: [] })
                  }}
                  icon={<BookOpen className="h-3.5 w-3.5 text-muted-foreground" />}
                />
              </div>

              <div>
                <Label className="text-[11px] text-muted-foreground mb-1 block">Chapter</Label>
                <MultiSelectPopover
                  title="Select Chapters"
                  options={filteredChapterOptions}
                  selectedValues={selectedChapters}
                  onSelectionChange={(newVal) => {
                    setSelectedChapters(newVal)
                    applyFilters({ chapter_ids: newVal })
                  }}
                  icon={<BookOpen className="h-3.5 w-3.5 text-muted-foreground" />}
                />
              </div>

              <div>
                <Label className="text-[11px] text-muted-foreground mb-1 block">Standard</Label>
                <MultiSelectPopover
                  title="Select Standards"
                  options={availableStandards}
                  selectedValues={selectedStandards}
                  onSelectionChange={(newVal) => {
                    setSelectedStandards(newVal)
                    applyFilters({ standard_ids: newVal })
                  }}
                  icon={<Layers className="h-3.5 w-3.5 text-muted-foreground" />}
                />
              </div>

              <div>
                <Label className="text-[11px] text-muted-foreground mb-1 block">Syllabus</Label>
                <MultiSelectPopover
                  title="Select Syllabi"
                  options={availableSyllabi}
                  selectedValues={selectedSyllabi}
                  onSelectionChange={(newVal) => {
                    setSelectedSyllabi(newVal)
                    applyFilters({ syllabus_ids: newVal })
                  }}
                  icon={<BookOpen className="h-3.5 w-3.5 text-muted-foreground" />}
                />
              </div>

              <div>
                <Label className="text-[11px] text-muted-foreground mb-1 block">Access</Label>
                <MultiSelectPopover
                  title="Select Access"
                  options={accessOptions}
                  selectedValues={selectedAccess}
                  onSelectionChange={(newVal) => {
                    setSelectedAccess(newVal)
                    applyFilters({ access_types: newVal })
                  }}
                />
              </div>

              <div>
                <Label className="text-[11px] text-muted-foreground mb-1 block">Duration</Label>
                <MultiSelectPopover
                  title="Select Duration"
                  options={durationOptions}
                  selectedValues={selectedDurations}
                  onSelectionChange={(newVal) => {
                    setSelectedDurations(newVal)
                    applyFilters({ duration_ranges: newVal })
                  }}
                  icon={<Clock className="h-3.5 w-3.5 text-muted-foreground" />}
                />
              </div>
            </div>
          )}

          {/* Active Filter Chips */}
          {totalActiveFiltersCount > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t text-xs">
              <span className="text-[11px] text-muted-foreground font-medium mr-1">Active Filters:</span>

              {selectedStatuses.map((st) => (
                <Badge key={`st-${st}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                  Status: {st.replace('_', ' ')}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={() => {
                      const updated = selectedStatuses.filter((s) => s !== st)
                      setSelectedStatuses(updated)
                      applyFilters({ statuses: updated })
                    }}
                  />
                </Badge>
              ))}

              {selectedTypes.map((tt) => (
                <Badge key={`tt-${tt}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                  Type: {TEST_TYPE_LABELS[tt] || tt.replace('_', ' ')}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={() => {
                      const updated = selectedTypes.filter((t) => t !== tt)
                      setSelectedTypes(updated)
                      applyFilters({ test_types: updated })
                    }}
                  />
                </Badge>
              ))}

              {selectedExams.map((exId) => {
                const ex = availableExams.find((e) => String(e.id) === String(exId))
                return (
                  <Badge key={`ex-${exId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Exam: {ex?.label || `#${exId}`}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedExams.filter((id) => id !== exId)
                        setSelectedExams(updated)
                        applyFilters({ exam_ids: updated })
                      }}
                    />
                  </Badge>
                )
              })}

              {selectedLangs.map((l) => (
                <Badge key={`lang-${l}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2 uppercase">
                  Lang: {l}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={() => {
                      const updated = selectedLangs.filter((lang) => lang !== l)
                      setSelectedLangs(updated)
                      applyFilters({ languages: updated })
                    }}
                  />
                </Badge>
              ))}

              {selectedAccess.map((acc) => (
                <Badge key={`acc-${acc}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2 capitalize">
                  Access: {acc}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={() => {
                      const updated = selectedAccess.filter((a) => a !== acc)
                      setSelectedAccess(updated)
                      applyFilters({ access_types: updated })
                    }}
                  />
                </Badge>
              ))}

              {selectedDurations.map((dur) => (
                <Badge key={`dur-${dur}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                  Duration: {DURATION_LABELS[dur] || dur}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={() => {
                      const updated = selectedDurations.filter((d) => d !== dur)
                      setSelectedDurations(updated)
                      applyFilters({ duration_ranges: updated })
                    }}
                  />
                </Badge>
              ))}

              {selectedSubjects.map((sId) => {
                const s = availableSubjects.find((subj) => String(subj.id) === String(sId))
                return (
                  <Badge key={`subj-${sId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Subject: {s?.label || `#${sId}`}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedSubjects.filter((id) => id !== sId)
                        setSelectedSubjects(updated)
                        applyFilters({ subject_ids: updated })
                      }}
                    />
                  </Badge>
                )
              })}

              {selectedChapters.map((cId) => {
                const c = availableChaptersAll.find((chap) => String(chap.id) === String(cId))
                return (
                  <Badge key={`chap-${cId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Chapter: {c?.name || `#${cId}`}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedChapters.filter((id) => id !== cId)
                        setSelectedChapters(updated)
                        applyFilters({ chapter_ids: updated })
                      }}
                    />
                  </Badge>
                )
              })}

              {selectedStandards.map((stId) => {
                const st = availableStandards.find((std) => String(std.id) === String(stId))
                return (
                  <Badge key={`std-${stId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Standard: {st?.label || `#${stId}`}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedStandards.filter((id) => id !== stId)
                        setSelectedStandards(updated)
                        applyFilters({ standard_ids: updated })
                      }}
                    />
                  </Badge>
                )
              })}

              {selectedSyllabi.map((sylId) => {
                const sy = availableSyllabi.find((s) => String(s.id) === String(sylId))
                return (
                  <Badge key={`syl-${sylId}`} variant="secondary" className="gap-1 text-[11px] font-normal py-0.5 px-2">
                    Syllabus: {sy?.label || `#${sylId}`}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => {
                        const updated = selectedSyllabi.filter((id) => id !== sylId)
                        setSelectedSyllabi(updated)
                        applyFilters({ syllabus_ids: updated })
                      }}
                    />
                  </Badge>
                )
              })}
            </div>
          )}
        </div>

        {/* Top Pagination */}
        {meta && (
          <Pagination
            meta={meta}
            onPageChange={(page) => applyFilters({ page })}
            onPerPageChange={(per_page) => applyFilters({ page: 1, per_page })}
            itemLabel="tests"
          />
        )}

        {/* Tests Table */}
        <Card className="overflow-visible">
          <CardContent className="p-0 overflow-visible">
            <Table className="overflow-visible">
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-28">Type</TableHead>
                  <TableHead className="w-20">Lang</TableHead>
                  <TableHead className="w-24">Questions</TableHead>
                  <TableHead className="w-24">Duration</TableHead>
                  <TableHead className="w-24">Access</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-16 text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No tests match the selected filters. Try clearing some filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  tests.map((t, idx) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium space-y-1 py-3">
                        <Link href={`/admin/tests/${t.id}`} className="hover:underline text-sm font-semibold">
                          {t.title}
                        </Link>
                        {t.exam && <div className="text-xs text-muted-foreground">{t.exam}</div>}

                        <TestContextBadges
                          mappedExams={t.mapped_exams}
                          mappedSubjects={t.mapped_subjects}
                          mappedChapters={t.mapped_chapters}
                          mappedSections={t.mapped_sections}
                          mappedStandards={t.mapped_standards}
                          attemptsCount={t.attempts_count}
                          uniqueUsersCount={t.unique_users_count}
                          totalCreditsGenerated={t.total_credits_generated}
                          activatedAt={t.activated_at}
                          returnUrl={typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/admin/tests'}
                          isFirstRow={idx === 0}
                          isLastRow={idx > 0 && idx >= tests.length - 2}
                        />
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{TEST_TYPE_LABELS[t.test_type] || t.test_type.replace('_', ' ')}</Badge></TableCell>
                      <TableCell className="uppercase text-xs font-semibold">
                        <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{t.language}</span>
                      </TableCell>
                      <TableCell className="text-sm">{t.question_count} Qs</TableCell>
                      <TableCell className="text-sm">{t.duration_minutes}m</TableCell>
                      <TableCell>
                        {t.is_free ? (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 inline-flex items-center gap-1 font-bold text-xs">
                            <Coins className="h-3 w-3 shrink-0 text-emerald-500" />
                            0
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 inline-flex items-center gap-1 font-bold text-xs">
                            <Coins className="h-3 w-3 shrink-0 text-amber-500" />
                            {t.credit_cost}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {t.status === 'published' ? (
                          <Badge variant="outline" title="Published" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 p-1.5 rounded-full inline-flex items-center justify-center">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                          </Badge>
                        ) : t.status === 'archived' ? (
                          <Badge variant="outline" title="Archived" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 p-1.5 rounded-full inline-flex items-center justify-center">
                            <Archive className="h-4 w-4 text-amber-500 shrink-0" />
                          </Badge>
                        ) : (
                          <Badge variant="outline" title="Draft" className="bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20 p-1.5 rounded-full inline-flex items-center justify-center">
                            <FileEdit className="h-4 w-4 text-slate-500 shrink-0" />
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {t.status !== 'published' && (
                            <Button size="icon" variant="ghost" asChild title="Edit in Test Generator">
                              <Link href={`/admin/test_generator?test_id=${t.id}`}>
                                <FileEdit className="h-4 w-4 text-primary" />
                              </Link>
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" asChild title="View Test Details">
                            <Link href={`/admin/tests/${t.id}`}>
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Bottom Pagination */}
        {meta && (
          <Pagination
            meta={meta}
            onPageChange={(page) => applyFilters({ page })}
            onPerPageChange={(per_page) => applyFilters({ page: 1, per_page })}
            itemLabel="tests"
          />
        )}
      </div>
    </AppShell>
  )
}
